import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ToolCallData } from '../../api/types'
import { cn } from '../../lib/cn'
import { IconCheck, IconChevronRight, IconStop, IconWarning } from '../icons'
import { Spinner } from '../ui/Spinner'
import { type SubagentLiveInfo, toolState } from './feed'
import { Markdown } from './Markdown'

interface SubagentRowProps {
  tool: ToolCallData
  subagent?: SubagentLiveInfo
}

function parseTaskOutput(output?: string): { taskId?: string; state?: string; reason?: string; content: string } {
  if (!output) return { content: '' }
  const match = output.match(/^<task(?:\s+id="([^"]*)")?(?:\s+state="([^"]*)")?(?:\s+reason="([^"]*)")?>\s*([\s\S]*?)\s*<\/task>$/)
  if (match) {
    return {
      taskId: match[1],
      state: match[2],
      reason: match[3],
      content: match[4]?.trim() ?? ''
    }
  }
  return { content: output.trim() }
}

export function SubagentRow({ tool, subagent }: SubagentRowProps) {
  const { t } = useTranslation()
  const rawState = toolState(tool)
  const parsedOutput = parseTaskOutput(tool.output)
  // A run the user stopped is neither a success nor a failure: the loop reports
  // `cancelled`, the persisted result says reason="stopped", and a call that
  // never started carries the loop's abort error.
  const isStopped = subagent?.state === 'cancelled' || parsedOutput.reason === 'stopped' || tool.error === 'aborted by user'
  // The model announced the call but is still writing its arguments: the card has
  // no prompt, no subagent and nothing to show yet — say so instead of looking stuck.
  const isPreparing = !isStopped && tool.streamingInput === true
  const isRunning = !isStopped && (subagent?.state === 'running' || (!subagent && rawState === 'running'))
  const isError = !isStopped && !isRunning && (subagent?.state === 'error' || rawState === 'error')
  // Out of steps or cut off at the token cap: the text is partial, not a result.
  const isPartial = !isStopped && !isRunning && !isError && parsedOutput.state === 'incomplete'
  const isCompleted = !isStopped && !isRunning && !isError && !isPartial && (subagent?.state === 'completed' || rawState === 'done')

  // Auto-open when actively running; respect user click afterwards
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const [showFullPrompt, setShowFullPrompt] = useState(false)
  const [showThinking, setShowThinking] = useState(false)

  // Nothing to open while the prompt is still being written.
  const open = userOpen ?? (isRunning && !isPreparing)

  const input = (tool.input || {}) as {
    description?: string
    prompt?: string
    subagent_type?: string
    background?: boolean
    task_id?: string
  }

  const subagentType = subagent?.subagentType || input.subagent_type || 'general'
  const title = input.description || (input.prompt ? input.prompt.split('\n')[0].slice(0, 80) : t('chat.subagent.title'))
  const promptText = input.prompt || ''

  // The tool output is the wrapper XML (or, for a background hand-off, "running in
  // background"); the live entry is what carries the text while it runs.
  const liveText = subagent?.text ?? ''
  const displayResult = subagent?.result || (isRunning || isStopped ? liveText : parsedOutput.content || liveText)
  const isStreamingResult = isRunning && Boolean(subagent?.text)
  const reasoning = subagent?.reasoning

  return (
    <li className="list-none border-b border-line/60 last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setUserOpen(!open)}
        className={cn(
          'flex min-h-10 w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-cream-100/80',
          isRunning && 'bg-cream-100/40'
        )}
      >
        <IconChevronRight
          width={14}
          height={14}
          className={cn('shrink-0 text-ink-500 transition-transform duration-150', open && 'rotate-90')}
        />

        {/* Subagent Type Badge */}
        <span className="inline-flex shrink-0 items-center rounded-md bg-blue-600 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-white uppercase shadow-xs">
          {subagentType}
        </span>

        {/* Description / Summary */}
        <span className="min-w-0 flex-1 truncate font-medium text-ink-300">
          {title}
        </span>

        {/* Live indicator / status */}
        <div className="flex shrink-0 items-center gap-2">
          {isPreparing ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ginger-500/10 px-2 py-0.5 font-mono text-[11px] font-medium text-ginger-600">
              <Spinner size="xs" className="text-ginger-600" />
              <span className="animate-pulse">{t('chat.subagent.preparing')}</span>
            </span>
          ) : isRunning ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ginger-500/10 px-2 py-0.5 font-mono text-[11px] font-medium text-ginger-600">
              <Spinner size="xs" className="text-ginger-600" />
              {subagent?.lastTool ? (
                <span>{subagent.lastTool}</span>
              ) : reasoning && !subagent?.text ? (
                <span className="animate-pulse">{t('chat.subagent.thinking')}</span>
              ) : (
                <span className="animate-pulse">{t('chat.subagent.running')}</span>
              )}
            </span>
          ) : isError ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600">
              <IconWarning width={13} height={13} />
              <span>{t('chat.subagent.failed')}</span>
            </span>
          ) : isStopped ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-400">
              <IconStop width={12} height={12} />
              <span>{t('chat.subagent.stopped')}</span>
            </span>
          ) : isPartial ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ginger-600">
              <IconWarning width={13} height={13} />
              <span>{t('chat.subagent.partial')}</span>
            </span>
          ) : isCompleted ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-mint-500">
              <IconCheck width={13} height={13} />
              <span>{t('chat.subagent.completed')}</span>
            </span>
          ) : null}
        </div>
      </button>

      {open && (
        <div className="space-y-3 bg-cream-100/30 px-3 pt-1 pb-3 pl-8 text-xs">
          {/* Prompt / Goal Section */}
          {promptText && (
            <div className="rounded-lg border border-line/60 bg-cream-50/80 p-2.5">
              <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-ink-400">
                <span>{t('chat.subagent.prompt')}</span>
                {promptText.length > 160 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowFullPrompt(p => !p)
                    }}
                    className="text-[10px] text-ginger-600 hover:underline"
                  >
                    {showFullPrompt ? t('chat.subagent.hidePrompt') : t('chat.subagent.showPrompt')}
                  </button>
                )}
              </div>
              <p className={cn('text-ink-400 whitespace-pre-wrap', !showFullPrompt && promptText.length > 160 && 'line-clamp-2')}>
                {promptText}
              </p>
            </div>
          )}

          {/* Subagent Tools History */}
          {subagent?.toolCalls && subagent.toolCalls.length > 0 && (() => {
            const uniqueTools = Array.from(new Set(subagent.toolCalls))
            return (
              <div className="flex flex-wrap items-center gap-1.5 py-1">
                <span className="text-[11px] font-medium text-ink-400">{t('chat.subagent.toolsUsed')}:</span>
                {uniqueTools.map((tName) => {
                  const isCurrent = isRunning && tName === subagent.lastTool
                  return (
                    <span
                      key={tName}
                      className={cn(
                        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px]',
                        isCurrent
                          ? 'border border-ginger-500/30 bg-ginger-500/10 font-semibold text-ginger-600'
                          : 'border border-line/70 bg-cream-100 text-ink-400'
                      )}
                    >
                      {isCurrent && <Spinner size="xs" />}
                      {tName}
                    </span>
                  )
                })}
              </div>
            )
          })()}

          {/* Subagent Reasoning / Thinking */}
          {reasoning && (
            <div className="rounded-lg border border-line/60 bg-cream-50/60 p-2">
              <button
                type="button"
                onClick={() => setShowThinking(t => !t)}
                className="flex w-full items-center justify-between text-[11px] font-medium text-ink-400 hover:text-ink-200"
              >
                <span className={cn('italic', isRunning && !subagent?.text && 'animate-pulse')}>
                  {t('chat.subagent.thinking')}
                </span>
                <span className="text-[10px]">{showThinking ? '−' : '+'}</span>
              </button>
              {(showThinking || (isRunning && !subagent?.text)) && (
                <p className="mt-1.5 max-h-40 overflow-y-auto font-mono text-[11px] whitespace-pre-wrap text-ink-400 italic">
                  {reasoning}
                </p>
              )}
            </div>
          )}

          {/* Subagent Output (Live or Final Result) */}
          {(displayResult || isStreamingResult) && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold text-ink-400">
                {isStopped || isPartial ? t('chat.subagent.partialOutput') : t('chat.subagent.output')}
              </div>
              <div className="max-h-96 overflow-y-auto rounded-lg border border-line/80 bg-cream-50 p-3 text-ink-200">
                <Markdown text={displayResult} />
                {isStreamingResult && (
                  <span className="inline-block h-3.5 w-1.5 animate-pulse bg-ginger-500 align-middle ml-1" />
                )}
              </div>
            </div>
          )}

          {/* Error Message if any — an aborted call's error is not a failure */}
          {tool.error && !isStopped && (
            <pre className="max-h-48 overflow-auto rounded-lg bg-red-50 p-2.5 font-mono text-xs whitespace-pre-wrap text-red-700">
              {tool.error}
            </pre>
          )}
        </div>
      )}
    </li>
  )
}
